import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useStoreActions, useStoreState } from '../../models/RootStore';
import useBacNet from '../useBacNet';
import useCoolPoll from '../useCoolPoll';

/**
 * Hook for managing BACnet schedules CRUD operations
 * Handles fetching, creating, updating, and deleting schedules
 */
const useBacnetSchedules = (bacnetUnitId) => {
  // State management
  const [schedules, setSchedules] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [schedulesInitialized, setSchedulesInitialized] = useState(false);
  const [error, setError] = useState(null);

  // Store actions
  const {
    getBacNetSchedules,
    updateBacNetSchedule: updateScheduleAction,
    deleteBacNetSchedule: deleteScheduleAction,
    createBacNetSchedule,
    getSchedule,
  } = useStoreActions((actions) => actions.schedules);

  const addMessage = useStoreActions((actions) => actions.errorMessage.addMessage);

  // Get BACnet units data
  const { siteBacNetUnits } = useBacNet();
  const selections = useStoreState((s) => s.selections.selections);
  const { siteId: selectedSiteId } = selections;

  // Get BACnet unit from store
  const bacnetUnit = useMemo(() => {
    if (!bacnetUnitId || !selectedSiteId) {
      return null;
    }
    return siteBacNetUnits?.[selectedSiteId]?.[bacnetUnitId];
  }, [bacnetUnitId, selectedSiteId, siteBacNetUnits]);

  // Fetch schedules for the BACnet unit
  const fetchSchedules = useCallback(async ({ silent = false } = {}) => {
    if (!bacnetUnitId) {
      setError('No BACnet unit ID provided');
      return;
    }

    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);
      
      const response = await getBacNetSchedules({
        bacnetId: bacnetUnitId,
        type: '0',
      });
      
      // Extract schedules data from the response structure
      const fetchedSchedules = response?.data || response;
      setSchedules(fetchedSchedules);
      setSchedulesInitialized(true);
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch schedules';
      setError(errorMessage);
      addMessage({ message: errorMessage });
    } finally {
      if (!silent){ 
        setIsLoading(false);
      }
    }
  }, [bacnetUnitId, getBacNetSchedules, addMessage]);

  // Keep ref to schedules for stable polling callback
  const schedulesRef = useRef(schedules);
  useEffect(() => {
    schedulesRef.current = schedules;
  }, [schedules]);

  // Create a new schedule
  const createSchedule = useCallback(async (scheduleData) => {
    if (!bacnetUnitId) {
      throw new Error('No BACnet unit ID provided');
    }

    try {
      setError(null);

      const response = await createBacNetSchedule({
        bacnetId: bacnetUnitId,
        data: scheduleData,
      });

      // Extract created schedule from response structure
      const createdSchedule = response?.data || response;

      // Update local schedules state
      setSchedules((prevSchedules) => ({
        ...prevSchedules,
        [createdSchedule.id]: createdSchedule,
      }));

      return createdSchedule;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create schedule';
      setError(errorMessage);
      addMessage({ message: errorMessage });
      throw err;
    }
  }, [bacnetUnitId, createBacNetSchedule, addMessage]);

  // Update an existing schedule
  const updateSchedule = useCallback(async (scheduleId, updateData) => {
    try {
      setError(null);

      const response = await updateScheduleAction({
        id: scheduleId,
        data: updateData,
      });

      // Extract updated schedule from response structure
      const updatedSchedule = response?.data || response;

      // Update local schedules state
      setSchedules((prevSchedules) => ({
        ...prevSchedules,
        [scheduleId]: {
          ...prevSchedules?.[scheduleId],
          ...updatedSchedule,
        },
      }));

      return updatedSchedule;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update schedule';
      setError(errorMessage);
      addMessage({ message: errorMessage });
      throw err;
    }
  }, [updateScheduleAction, addMessage]);

  // Delete a schedule
  const deleteSchedule = useCallback(async (scheduleId) => {
    try {
      setError(null);

      await deleteScheduleAction(scheduleId);

      // Remove from local schedules state
      setSchedules((prevSchedules) => {
        if (!prevSchedules) return prevSchedules;
        // eslint-disable-next-line no-unused-vars
        const { [scheduleId]: deleted, ...remaining } = prevSchedules;
        return remaining;
      });

      return true;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete schedule';
      setError(errorMessage);
      addMessage({ message: errorMessage });
      throw err;
    }
  }, [deleteScheduleAction, addMessage]);

  // Toggle schedule active/inactive state
  const toggleScheduleState = useCallback(async (schedule) => {
    const { id, isDisabled } = schedule;
    return updateSchedule(id, { isDisabled: !isDisabled });
  }, [updateSchedule]);

  // Get schedule by ID
  const getScheduleById = useCallback((scheduleId) => {
    return schedules?.[scheduleId] || null;
  }, [schedules]);

  // Get schedules as array
  const schedulesArray = useMemo(() => {
    if (!schedules) return [];
    return Object.values(schedules);
  }, [schedules]);

  // Get active schedules
  const activeSchedules = useMemo(() => {
    return schedulesArray.filter(schedule => !schedule.isDisabled);
  }, [schedulesArray]);

  // Get inactive schedules
  const inactiveSchedules = useMemo(() => {
    return schedulesArray.filter(schedule => schedule.isDisabled);
  }, [schedulesArray]);

  // Initial fetch on mount or when bacnetUnitId changes
  useEffect(() => {
    if (bacnetUnitId) {
      fetchSchedules();
    }
  }, [bacnetUnitId, fetchSchedules]);

  // Check if any schedule is in syncing state
  const isAnyScheduleSyncing = useMemo(() => {
    return schedulesArray.some(schedule => schedule.isSynced === 1);
  }, [schedulesArray]);

  const pollSyncingSchedules = useCallback(async () => {
    const currentSchedules = schedulesRef.current;
    if (!currentSchedules) {
      return;
    }
    const syncingSchedules = Object.values(currentSchedules)
      .filter(s => s.isSynced === 1);

    if (syncingSchedules.length === 0) {
      return;
    }

    await Promise.all(syncingSchedules.map(async (schedule) => {
      try {
        const refreshedSchedule = await getSchedule(schedule.id);
        
        setSchedules((prevSchedules) => ({
          ...prevSchedules,
          [schedule.id]: {
            ...prevSchedules?.[schedule.id],
            ...refreshedSchedule,
          },
        }));
      } catch (err) {
        console.error(`Failed to poll schedule ${schedule.id}`, err);
      }
    }));
  }, [getSchedule]);

  // Polling for syncing schedules
  useCoolPoll({
    enabled: isAnyScheduleSyncing,
    onPoll: pollSyncingSchedules,
    frequency: 2000,
  });

  return {
    // Data
    schedules,
    schedulesArray,
    activeSchedules,
    inactiveSchedules,
    bacnetUnit,
    
    // State
    isLoading,
    schedulesInitialized,
    error,
    
    // Actions
    fetchSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    toggleScheduleState,
    getScheduleById,
    
    // Utilities
    refetch: fetchSchedules,
    clearError: () => setError(null),
  };
};

export default useBacnetSchedules;
